import {
  GrowthBook,
  GrowthBookProvider as GrowthBookProviderBase,
} from "@growthbook/growthbook-react";
import { autoAttributesPlugin } from "@growthbook/growthbook/plugins";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";

import { LazyLoading } from "@components/lazy-loading";
import { GROWTHBOOK_TRACKER_ID } from "@config/tracking-config";
import { useContainer } from "@contexts/container-context";
import { useCurrentUser } from "@hooks/queries/account";
import container from "@src/container";

const { snowplowTrackingService } = container;

// Create a GrowthBook instance
const gb = new GrowthBook({
  apiHost: process.env.GROWTHBOOK_API_HOST,
  clientKey: process.env.GROWTHBOOK_CLIENT_KEY,
  enableDevMode: true,
  // Only required for A/B testing
  // Called every time a user is put into an experiment
  trackingCallback: (experiment, result) => {
    const data = {
      experimentId: experiment.key,
      variationId: result.key,
      hashAttribute: result.hashAttribute,
      hashValue: result.hashValue,
    };

    if (process.env.NODE_ENV !== "production") {
      console.log("Experiment Viewed", data);
    }

    snowplowTrackingService.trackEvent(
      {
        event: {
          schema: "iglu:io.growthbook/experiment_viewed/jsonschema/1-0-0",
          data,
        },
      },
      [],
      [GROWTHBOOK_TRACKER_ID]
    );
  },
  plugins: [autoAttributesPlugin({ uuidKey: "autoId" })],
});
gb.init({ streaming: true });

export const GrowthBookProvider = ({ children }) => {
  const [ready, setReady] = useState(false);

  const currentUser = useCurrentUser();
  const {
    toggles: { isGrowthbookEnabled },
  } = useContainer();

  useEffect(() => {
    // Clear attributes when the component unmounts
    return () => {
      gb.setAttributes({});
    };
  }, []);

  useEffect(() => {
    if (!isGrowthbookEnabled || !currentUser) {
      // Clear attributes if growthbook is not enabled or user is not logged in
      gb.setAttributes({});
      setReady(true);
      return;
    }

    // Safety net to make sure the children are not rendered before the feature
    // flags are configured for the user
    setReady(false);

    // Whitelist only the attributes GrowthBook needs for targeting and
    // bucketing. Spreading the whole currentUser would leak every user
    // property (tokens, PII) to the feature-flag service.
    gb.setAttributes({
      id: currentUser.userId,
      userId: currentUser.userId,
      organizationCode: currentUser.organizationCode,
      parentOrganizationCode: currentUser.parentOrganizationCode,
      roleId: currentUser.roleId,
    }).then(() => {
      setReady(true);
    });
  }, [currentUser]);

  return (
    <GrowthBookProviderBase growthbook={gb}>
      {ready ? children : <LazyLoading />}
    </GrowthBookProviderBase>
  );
};

GrowthBookProvider.propTypes = {
  children: PropTypes.node,
};
